/* eslint-disable indent */
import * as React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Label from "../../../../components/label";
import { useSharedContext } from "../../../../shared/SharedComponentsContext";
import MenuPopover from "../../../../components/menu-popover";
import { IconButton, MenuItem } from "@mui/material";
import Iconify from "../../../../components/iconify";
import ConfirmDialog from "../../../../components/confirm-dialog";
import { dbQuote } from "../../../../api/parnerus/types/quote";
import { fCurrency } from "../../../../utils/formatNumber";
import { format } from "date-fns";
import { fDate } from "../../../../utils/formatTime";

type Props = {
  row: dbQuote;
  onDeleteRow?: VoidFunction;
};

export default function QuoteCard({ row, onDeleteRow }: Props) {
  const { openQuoteViewSlider, openQuoteEditSlider } = useSharedContext();

  const [openConfirm, setOpenConfirm] = React.useState(false);

  const [openPopover, setOpenPopover] = React.useState<HTMLElement | null>(
    null
  );

  const handleOpenConfirm = () => {
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  return (
    <Box sx={{ minWidth: 275 }}>
      <Card
        variant="outlined"
        sx={{ mt: 2, minHeight: 210, position: "relative" }}
        onClick={() => openQuoteViewSlider(row)}
      >
        <Box sx={{ position: "absolute", top: 8, right: 8 }}>
          <IconButton
            color={openPopover ? "inherit" : "default"}
            onClick={handleOpenPopover}
          >
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>
        </Box>
        <React.Fragment>
          <CardContent>
            <Typography
              gutterBottom
              sx={{ color: "text.secondary", fontSize: 14 }}
            >
              Cotización
            </Typography>
            <Typography>
              {fCurrency(
                row.products.reduce((prev, current) => prev + current.total, 0)
              )}
            </Typography>
            <Typography variant="h5" textTransform="capitalize" component="div">
              {row.contactName}
            </Typography>
            {row.companyName && (
              <Typography
                textTransform="capitalize"
                sx={{ color: "text.secondary", mb: 1.5 }}
              >
                {row.companyName}
              </Typography>
            )}
            <Typography sx={{ color: "text.secondary", mt: 1.5, mb: 1.5 }}>
              #{row.quoteId.split("-")[0].toUpperCase()}
            </Typography>

            {row.status && (
              <Label
                variant="soft"
                color={
                  (row.status === "Accepted" && "success") ||
                  (row.status === "Sent to customer" && "warning") ||
                  (row.status === "Declined" && "error") ||
                  "default"
                }
                sx={{ mr: 2 }}
              >
                {row.status}
              </Label>
            )}
            {row.dueDate < format(new Date(), "yyyy-MM-dd") &&
            row.status !== "Accepted" &&
            row.status !== "Declined" ? (
              <Label variant="soft" color="error">
                {fDate(row.dueDate)}
              </Label>
            ) : (
              fDate(row.dueDate)
            )}
          </CardContent>
        </React.Fragment>
      </Card>

      <MenuPopover
        open={openPopover}
        onClose={handleClosePopover}
        arrow="right-top"
        sx={{ width: 140 }}
      >
        <MenuItem
          onClick={() => {
            handleClosePopover();
            openQuoteViewSlider(row);
          }}
        >
          <Iconify icon="clarity:details-line" />
          Detalles
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleClosePopover();
            openQuoteEditSlider(row);
          }}
        >
          <Iconify icon="eva:edit-fill" />
          Editar
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleOpenConfirm();
            handleClosePopover();
          }}
          sx={{ color: "error.main" }}
        >
          <Iconify icon="eva:trash-2-outline" />
          Eliminar
        </MenuItem>
      </MenuPopover>

      <ConfirmDialog
        open={openConfirm}
        onClose={handleCloseConfirm}
        title="Eliminar"
        content="¿Estás seguro que deseas eliminar este registro? Esto deshabilitará el registro, y sólo quedará visible para los registros que estén relacionado al mismo, pero no podrás encontrarlo más en este listado"
        action={
          <Button variant="contained" color="error" onClick={onDeleteRow}>
            Eliminar
          </Button>
        }
      />
    </Box>
  );
}
